/**
 * Rebuilt ATO RBA ledger detail card — rendered in the main ATO/SRO view (full
 * content width) for any authority that has a rebuilt Running Balance Account
 * (MOR-587). Shows the balance-over-time chart, the GST/PAYG/interest/penalty
 * debt breakdown (a summary of the movements), and the cleaned movement rows as
 * an expandable detail table.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ChevronDown, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { cn } from "@/utils/misc";
import { RunningBalanceChart } from "./RunningBalanceChart";
import type { AtoSroEntryUI } from "./types";

interface LedgerRow {
  _id: string;
  effectiveDate: string;
  processDate?: string;
  description: string;
  debit: number;
  credit: number;
  rebuiltBalance: number;
  category: string;
  kind: "movement" | "breakdown_child" | "detail";
  /** Which ATO account the row came from (ICA / ITA); legacy rows unset. */
  accountType?: string;
}

const CATEGORY_LABELS: Array<{ key: string; label: string }> = [
  { key: "gst", label: "GST" },
  { key: "payg", label: "PAYG withholding" },
  { key: "incomeTax", label: "Income tax" },
  { key: "interest", label: "Interest (GIC)" },
  { key: "penalty", label: "Penalties" },
  { key: "payment", label: "Payments applied" },
  { key: "other", label: "Other" },
];

const CATEGORY_BADGE: Record<string, string> = {
  gst: "GST",
  payg: "PAYG",
  income_tax: "Income Tax",
  interest: "GIC",
  penalty: "Penalty",
  payment: "Payment",
  statement: "Statement",
  other: "Other",
};

const CATEGORY_COLOR: Record<
  string,
  "blue" | "violet" | "amber" | "red" | "emerald" | "gray"
> = {
  gst: "violet",
  payg: "blue",
  income_tax: "blue",
  interest: "amber",
  penalty: "red",
  payment: "emerald",
  statement: "gray",
  other: "gray",
};

/** Short + long labels for the ATO account tabs (MOR-875). */
const ACCOUNT_SHORT: Record<string, string> = {
  ica: "ICA",
  ita: "ITA",
  other: "Other",
};

const ACCOUNT_LONG: Record<string, string> = {
  ica: "Integrated Client Account (ICA)",
  ita: "Income Tax Account (ITA)",
  other: "Other ATO account",
};

const ACCOUNT_ORDER: Record<string, number> = { ica: 0, ita: 1, other: 2 };

function fmt(v: number | null | undefined): string {
  if (v == null) return "N/A";
  return "$" + Number(v).toLocaleString("en-AU", { minimumFractionDigits: 0 });
}

/**
 * A balance for display: positive = owing (DR), negative = the account is in
 * credit — shown explicitly as "CR" rather than a bare minus, so arrears and
 * credit can never be confused (MOR-807).
 */
function fmtBalance(v: number | null | undefined): string {
  if (v == null) return "N/A";
  if (v < 0) return fmt(Math.abs(v)) + " CR";
  return fmt(v);
}

/**
 * ATO-only first-pass insolvency estimate, derived purely from the RBA: the
 * date from which the running balance never again fell below half its peak —
 * i.e. the tax debt locked in at a high level and stopped being brought under
 * control. A transparent heuristic (ticket requirement 3a), not a determination.
 */
function computeAtoInsolvency(
  rows: LedgerRow[],
): { date: string; peak: number; floor: number } | null {
  const mv = rows.filter((r) => r.kind === "movement" && r.effectiveDate);
  if (mv.length < 4) return null;
  const peak = mv.reduce((max, r) => Math.max(max, r.rebuiltBalance), 0);
  if (peak <= 0) return null;
  const floor = peak / 2;
  const lastBelow = mv.reduce(
    (last, r, i) => (r.rebuiltBalance < floor ? i : last),
    -1,
  );
  const onset = lastBelow + 1;
  if (onset >= mv.length) return null;
  return { date: mv[onset].effectiveDate, peak, floor };
}

function basisLabel(basis: string): string {
  switch (basis) {
    case "practitioner_override":
      return "Practitioner override";
    case "books_records_inadequacy":
      return "Books & Records (s588E)";
    case "financial_indicators":
      return "Financial indicators";
    default:
      return "Estimated";
  }
}

export function LedgerDetailCard({ entry }: { entry: AtoSroEntryUI }) {
  const [showMovements, setShowMovements] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);
  // Active account tab — "combined" (the default) or an accountType (MOR-875).
  const [account, setAccount] = useState<string>("combined");

  const { data: rowsData } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoLedgerForEntry, {
      entryId: entry._id as Id<"atoSroEntries">,
    }),
  );

  const { data: insolvency } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getInsolvencyDateForJob, {
      jobId: entry.jobId,
    }),
  );

  const rows = useMemo(() => rowsData ?? [], [rowsData]);

  const accounts = useMemo(
    () =>
      [...(entry.ledgers ?? [])].sort(
        (a, b) =>
          (ACCOUNT_ORDER[a.accountType] ?? 9) -
          (ACCOUNT_ORDER[b.accountType] ?? 9),
      ),
    [entry.ledgers],
  );
  const multiAccount = accounts.length > 1;
  const combinedView = !multiAccount || account === "combined";

  // Combined series: rows arrive globally sorted by effective date, but the
  // per-account rebuiltBalance values cannot be concatenated — recompute the
  // running balance across every account's movements merged together.
  const combinedRows = useMemo(() => {
    if (!multiAccount) return rows;
    let run = 0;
    return rows.map((r) =>
      r.kind === "movement"
        ? {
            ...r,
            rebuiltBalance: Math.round((run += r.debit - r.credit) * 100) / 100,
          }
        : r,
    );
  }, [rows, multiAccount]);

  const viewRows = useMemo(
    () =>
      combinedView
        ? combinedRows
        : rows.filter((r) => (r.accountType ?? "other") === account),
    [combinedView, combinedRows, rows, account],
  );

  // The ATO-only insolvency heuristic always reads the combined debt curve —
  // the company's exposure is the total across accounts.
  const atoInsolvency = useMemo(
    () => computeAtoInsolvency(combinedRows),
    [combinedRows],
  );

  const ledger = entry.ledger;
  if (!ledger) return null;

  const activeAccount = combinedView
    ? null
    : (accounts.find((a) => a.accountType === account) ?? null);
  const activeLedger = activeAccount ?? ledger;

  const headerTitle = multiAccount
    ? combinedView
      ? "All ATO accounts — consolidated"
      : (activeAccount?.accountLabel ?? ACCOUNT_LONG[account] ?? "Account")
    : accounts[0]
      ? (accounts[0].accountLabel ?? ACCOUNT_LONG[accounts[0].accountType])
      : "Running Balance Account";

  const movements = viewRows.filter((r) => r.kind === "movement");
  const displayRows = showAllRows ? viewRows : movements;
  const totals = activeLedger.categoryTotals as Record<string, number>;

  const recPill =
    activeLedger.reconciledToPod === "matched"
      ? {
          icon: CheckCircle2,
          text: "Reconciles to Proof of Debt",
          cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400",
        }
      : activeLedger.reconciledToPod === "variance"
        ? {
            icon: AlertTriangle,
            text: `Differs from Proof of Debt by ${fmt(activeLedger.podVariance)}`,
            cls: "bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400",
          }
        : null;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pb-3 pt-5">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <Badge
              color={entry.authority === "ato" ? "blue" : "violet"}
              variant="soft"
              size="sm"
              className="font-bold tracking-wide"
            >
              {entry.authority === "ato" ? "ATO" : "SRO"}
            </Badge>
            <h3 className="text-sm font-semibold">
              {entry.authorityLabel} — {headerTitle}
            </h3>
          </div>
          <p
            className={cn(
              "text-2xl font-bold tabular-nums",
              (activeLedger.closingBalance ?? 0) < 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400",
            )}
          >
            {fmtBalance(activeLedger.closingBalance)}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Closing balance
            {activeLedger.asAtDate ? ` as at ${activeLedger.asAtDate}` : ""} ·
            rebuilt from {activeLedger.rowCount} movements
            {combinedView && multiAccount
              ? ` across ${accounts.length} accounts`
              : ""}
          </p>
        </div>
        {recPill && (
          <div
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium",
              recPill.cls,
            )}
          >
            <recPill.icon className="h-3.5 w-3.5" />
            {recPill.text}
          </div>
        )}
      </div>

      {/* Account tabs — combined (default) vs per-account (ICA / ITA), MOR-875 */}
      {multiAccount && (
        <div className="px-5 pb-3">
          <div className="inline-flex flex-wrap gap-0.5 rounded-lg bg-muted/50 p-0.5">
            <button
              type="button"
              onClick={() => setAccount("combined")}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors",
                combinedView
                  ? "bg-background font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Combined
              <span className="font-semibold tabular-nums">
                {fmtBalance(ledger.closingBalance)}
              </span>
            </button>
            {accounts.map((a) => (
              <button
                key={a.accountType}
                type="button"
                onClick={() => setAccount(a.accountType)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors",
                  !combinedView && account === a.accountType
                    ? "bg-background font-medium shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {ACCOUNT_SHORT[a.accountType] ?? a.accountType}
                <span className="font-semibold tabular-nums">
                  {fmtBalance(a.closingBalance)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="px-5 pb-2">
        <RunningBalanceChart
          rows={viewRows}
          atoInsolvencyDate={combinedView ? atoInsolvency?.date : undefined}
          caseInsolvencyDate={insolvency?.date}
        />
        <div className="mt-2 space-y-2">
          {combinedView && atoInsolvency && (
            <div className="flex items-start gap-2 rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2 dark:border-indigo-900 dark:bg-indigo-950/10">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <div className="min-w-0">
                <p className="text-xs font-medium">
                  Estimated insolvency (ATO-only): {atoInsolvency.date}
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                  First-pass indicator from the ATO ledger alone: from this date
                  the running balance never again fell below half its peak (
                  {fmt(atoInsolvency.peak)} peak → {fmt(atoInsolvency.floor)}{" "}
                  floor). The tax debt locked in at a high level and was no
                  longer being brought back under control — a sign the company
                  was relying on unpaid tax as working capital. This is a
                  heuristic flag, not a determination.
                </p>
              </div>
            </div>
          )}
          {insolvency?.date && (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium">
                  Case-wide estimated insolvency: {insolvency.date}
                  <Badge
                    color="amber"
                    variant="soft"
                    size="sm"
                    className="text-[9px]"
                  >
                    {basisLabel(insolvency.basis)}
                  </Badge>
                </p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                  {insolvency.note}
                </p>
                <p className="mt-1 text-[10px] leading-relaxed text-muted-foreground/70">
                  Derived from the Books &amp; Records assessment and financial
                  indicators across the whole case — overlaid here for
                  comparison with the ATO-only estimate.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Debt breakdown — summary of the movements */}
      <div className="px-5 pb-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Debt breakdown
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {CATEGORY_LABELS.map(({ key, label }) => {
            const value = totals?.[key] ?? 0;
            if (value === 0) return null;
            return (
              <div
                key={key}
                className="rounded-lg border border-border px-3 py-2"
              >
                <p className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
                  {label}
                </p>
                <p
                  className={cn(
                    "text-sm font-semibold tabular-nums",
                    value < 0 && "text-emerald-600 dark:text-emerald-400",
                  )}
                >
                  {fmt(value)}
                </p>
              </div>
            );
          })}
          <div
            className={cn(
              "rounded-lg border-2 px-3 py-2",
              (activeLedger.closingBalance ?? 0) < 0
                ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/10"
                : "border-rose-200 bg-rose-50/40 dark:border-rose-900 dark:bg-rose-950/10",
            )}
          >
            <p
              className={cn(
                "text-[10px] font-semibold uppercase tracking-wide",
                (activeLedger.closingBalance ?? 0) < 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-rose-600 dark:text-rose-400",
              )}
            >
              {(activeLedger.closingBalance ?? 0) < 0
                ? "In credit"
                : "Net owing"}
            </p>
            <p
              className={cn(
                "text-sm font-bold tabular-nums",
                (activeLedger.closingBalance ?? 0) < 0
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-rose-700 dark:text-rose-400",
              )}
            >
              {fmtBalance(activeLedger.closingBalance)}
            </p>
          </div>
        </div>
        <p className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground">
          GST / PAYG / income tax / interest / penalties are gross charges;
          payments applied are netted against the account.
        </p>
      </div>

      {/* Movements detail — expandable */}
      <Collapsible open={showMovements} onOpenChange={setShowMovements}>
        <CollapsibleTrigger className="group flex w-full items-center gap-2 border-t border-border px-5 py-2.5 text-left transition-colors hover:bg-muted/40">
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Cleaned ledger movements
          </span>
          <Badge variant="secondary" size="sm">
            {movements.length}
          </Badge>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border px-5 py-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">
                Sorted by effective date; balance recomputed from debits and
                credits (the ATO balance column is unreliable).
              </p>
              <button
                type="button"
                onClick={() => setShowAllRows((v) => !v)}
                className="ml-2 shrink-0 text-[11px] text-primary hover:underline"
              >
                {showAllRows
                  ? "Movements only"
                  : `Show all rows (${viewRows.length})`}
              </button>
            </div>
            <div className="max-h-[28rem] overflow-auto rounded-lg border border-border">
              <Table className="w-full text-xs">
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-transparent">
                    <TableHead className="h-8 whitespace-nowrap">
                      Effective
                    </TableHead>
                    {multiAccount && combinedView && (
                      <TableHead className="h-8 whitespace-nowrap">
                        Account
                      </TableHead>
                    )}
                    <TableHead className="h-8">Description</TableHead>
                    <TableHead className="h-8 whitespace-nowrap">
                      Category
                    </TableHead>
                    <TableHead className="h-8 whitespace-nowrap text-right">
                      Debit
                    </TableHead>
                    <TableHead className="h-8 whitespace-nowrap text-right">
                      Credit
                    </TableHead>
                    <TableHead className="h-8 whitespace-nowrap text-right">
                      Balance
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.map((r) => (
                    <TableRow key={r._id} className="hover:bg-muted/30">
                      <TableCell
                        className={cn(
                          "whitespace-nowrap py-1.5 tabular-nums",
                          r.kind !== "movement" && "text-muted-foreground",
                        )}
                      >
                        {r.effectiveDate}
                      </TableCell>
                      {multiAccount && combinedView && (
                        <TableCell className="whitespace-nowrap py-1.5">
                          <Badge
                            color={r.accountType === "ita" ? "violet" : "blue"}
                            variant="soft"
                            size="sm"
                            className="text-[9px] font-bold"
                          >
                            {ACCOUNT_SHORT[r.accountType ?? "other"] ?? "Other"}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell
                        className={cn(
                          "py-1.5",
                          r.kind !== "movement" && "text-muted-foreground",
                          r.kind === "breakdown_child" && "pl-5 italic",
                        )}
                      >
                        {r.description}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-1.5">
                        {CATEGORY_BADGE[r.category] && (
                          <Badge
                            color={CATEGORY_COLOR[r.category] ?? "gray"}
                            variant="soft"
                            size="sm"
                            className="text-[9px]"
                          >
                            {CATEGORY_BADGE[r.category]}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-red-600 dark:text-red-400">
                        {r.debit ? fmt(r.debit) : ""}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                        {r.credit ? fmt(r.credit) : ""}
                      </TableCell>
                      <TableCell className="py-1.5 text-right font-medium tabular-nums">
                        {r.kind === "movement" ? fmt(r.rebuiltBalance) : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
